import { useCreateBid } from "@/hooks/reservoir/use-create-bid";
import { useGetAssetBalance } from "@/hooks/use-get-asset-balance";
import { useGetErc20Balance } from "@/hooks/use-get-erc-20-balance";
import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import { useStringWithPlusMinus } from "@/hooks/useStringWithPlusMinus";
import { usdcContractAddress } from "@talentir/shared/abis/erc-20";
import { SHARE_TO_PERCENT_CONVERSION } from "@talentir/shared/config";
import Big from "big.js";
import { useMemo } from "react";
import { toast } from "sonner";
import type { Asset } from "../order-dialog-v2";
import { OrderCommon } from "./common";

export function CreateBid({
    asset,
    onComplete,
    onOnramp,
}: {
    asset: Asset;
    onComplete: () => void;
    onOnramp: () => void;
}) {
    const assetBalance = useGetAssetBalance(asset.id);
    const { createBid, isLoading } = useCreateBid();

    const { user } = useGetLoggedInUser();
    const usdcBalance = useGetErc20Balance(usdcContractAddress, "USD", user?.id);

    const [cost, , strippedCost, setRawCost] = useStringWithPlusMinus("-");
    const [percent, , strippedPercent, setRawPercent] = useStringWithPlusMinus("+");

    const price = useMemo(() => {
        try {
            return +Big(strippedCost).div(Big(strippedPercent)).toString();
        } catch {
            return 0;
        }
    }, [strippedPercent, strippedCost]);

    const costError = useMemo(() => {
        try {
            const costBig = Big(strippedCost);
            const usdcBalanceBig = Big(usdcBalance?.balanceString ?? 0);

            if (costBig.gt(usdcBalanceBig)) {
                return "Balance too low";
            }

            return undefined;
        } catch {
            return undefined;
        }
    }, [usdcBalance, strippedCost]);

    async function prepareAndCreateBid() {
        try {
            let costRaw: bigint | undefined;
            let quantityRaw: bigint | undefined;
            try {
                costRaw = BigInt(Big(strippedCost).mul(Big(10).pow(6)).round().toString());
            } catch {
                toast.error("Price Error", { description: "Please enter a valid price" });
                return;
            }

            try {
                quantityRaw = BigInt(Big(strippedPercent).mul(Big(SHARE_TO_PERCENT_CONVERSION)).round().toString());
            } catch {
                toast.error("Quantity Error", { description: "Please enter a valid amount" });
                return;
            }

            await createBid(asset.blockchainTokenId ?? "", quantityRaw, costRaw);
            onComplete();
        } catch {}
    }

    return (
        <OrderCommon
            asset={asset}
            cta="Create Bid"
            buttonLabel={costError == null ? "Create Bid" : "Top Up"}
            useConfirmDialog={costError == null}
            topCurrency="$"
            topLeftInfo={`Current Balance: ${usdcBalance?.balanceFormatted ?? "$0.00"}`}
            topLabel="You Pay USD"
            topValue={cost}
            onTopValueChange={setRawCost}
            topDecimals={2}
            bottomCurrency="%"
            bottomLeftInfo={`Current Ownership: ${assetBalance.assetBalanceString}%`}
            bottomLabel="You want %"
            bottomValue={percent}
            onBottomValueChange={setRawPercent}
            bottomDecimals={4}
            priceForOnePercent={`${price.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
            })}`}
            onComplete={() => {
                if (costError != null) {
                    onOnramp();
                } else {
                    void prepareAndCreateBid();
                }
            }}
            isLoading={isLoading}
        />
    );
}
